import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { useEffect, useState } from "react";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function formatUrl(url: string | unknown): string {
	// Handle null or undefined
	if (!url) {
		return "/";
	}

	// Handle array input (which can happen with JSON parsing issues)
	if (Array.isArray(url) && url.length > 0) {
		return formatUrl(url[0]);
	}

	// Handle string URLs
	if (typeof url === "string") {
		// Handle JSON string arrays
		if (url.startsWith("[") && url.endsWith("]")) {
			try {
				const parsedArray = JSON.parse(url);
				if (Array.isArray(parsedArray) && parsedArray.length > 0) {
					return formatUrl(parsedArray[0]);
				}
			} catch {
				// If JSON parsing fails, continue with normal string handling
			}
		}

		if (url.startsWith("https") || url.startsWith("http")) {
			return url;
		}
		return `/${url}`;
	}

	// Handle unexpected input
	return "/";
}

export const validateEmail = (value: string) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(value);
};

export const validatePhone = (value: string) => {
	const phoneRegex = /^\d{10}$/;
	return phoneRegex.test(value);
};

export const validateOtp = (value: string) => {
	const otpRegex = /^\d{5}$/;
	return otpRegex.test(value);
};

export const formatSlabRange = (start: number, end: number) => {
	const format = (val: number) => {
		if (val >= 10000000) return `${val / 10000000} Cr`;
		if (val >= 100000) return `${val / 100000} L`;
		return `${val / 1000} K`;
	};
	if (end === Number.POSITIVE_INFINITY) return `> ${format(start)}`;
	return end === null
		? `${format(start)} +`
		: `${format(start)} to ${format(end)}`;
};

export const getStepTitleText = (step: string) => {
	switch (step) {
		case "employee_details":
			return "Name & ID";
		case "address_selection":
			return "Select address";
		case "payment":
			return "Payment";
		default:
			return "Payment";
	}
};

export const useDebounce = (
	value: {
		[key: string]: string[];
	},
	delay: number,
) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};
export const toCamelCase = (str: string) => {
	return str
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

/**
 * Default fallback image URL used across the application
 */
export const DEFAULT_FALLBACK_IMAGE =
	"https://icemint.s3.ap-south-1.amazonaws.com/dev/images/coming-soon.png";

/**
 * Sanitizes an image source from various possible formats to a valid URL string
 * Handles arrays, JSON strings, and direct URLs consistently
 *
 * @param imageSource - The source that needs sanitization (can be array, string, JSON string, etc.)
 * @param fallbackImage - Optional fallback image URL if source is invalid, defaults to DEFAULT_FALLBACK_IMAGE
 * @returns A valid image URL string
 */
export const sanitizeImageSrc = (imageSource: unknown): string => {
	// Default fallback image to use if needed
	const defaultFallbackImage =
		"https://icemint.s3.ap-south-1.amazonaws.com/dev/images/coming-soon.png";

	// Handle null or undefined
	if (!imageSource) {
		return defaultFallbackImage;
	}

	try {
		// Handle actual arrays (most common case based on the logs)
		if (Array.isArray(imageSource) && imageSource.length > 0) {
			// Return the first valid URL from the array
			for (const item of imageSource) {
				if (typeof item === "string" && item.startsWith("http")) {
					return item;
				}
			}
			return defaultFallbackImage; // No valid URLs in array, use fallback
		}

		// Handle string (including JSON string arrays)
		if (typeof imageSource === "string") {
			// Handle empty strings
			if (!imageSource.trim()) {
				return defaultFallbackImage;
			}

			// Parse JSON string arrays and get first valid URL
			if (
				imageSource.trim().startsWith("[") &&
				imageSource.trim().endsWith("]")
			) {
				try {
					const parsed = JSON.parse(imageSource);
					if (Array.isArray(parsed) && parsed.length > 0) {
						for (const item of parsed) {
							if (typeof item === "string" && item.startsWith("http")) {
								return item;
							}
						}
					}
				} catch {
					// JSON parsing failed, continue
				}
				return defaultFallbackImage;
			}

			// Direct URL string (only if it's an actual URL)
			if (imageSource.startsWith("http")) {
				return imageSource;
			} else {
				return defaultFallbackImage;
			}
		}

		// Return fallback for any other format
		return defaultFallbackImage;
	} catch (e) {
		console.error("Error sanitizing image source:", e);
		return defaultFallbackImage;
	}
};

/**
 * Checks if localStorage is available and accessible
 */
export function isLocalStorageAvailable(): boolean {
	if (typeof window === "undefined") return false;
	try {
		// Check if localStorage exists and is not null
		if (!window.localStorage) return false;
		const test = "__localStorage_test__";
		window.localStorage.setItem(test, test);
		window.localStorage.removeItem(test);
		return true;
	} catch {
		return false;
	}
}

/**
 * Creates a safe storage wrapper that falls back to in-memory storage
 * when localStorage is unavailable (e.g., in Chrome WebView on Android)
 */
export function createSafeStorage(): Storage {
	// Create in-memory storage fallback
	const memoryStorage: Record<string, string> = {};
	const memoryStorageImpl: Storage = {
		getItem: (key: string) =>
			key in memoryStorage ? memoryStorage[key] : null,
		setItem: (key: string, value: string) => {
			memoryStorage[key] = value;
		},
		removeItem: (key: string) => {
			delete memoryStorage[key];
		},
		clear: () => {
			Object.keys(memoryStorage).forEach((key) => delete memoryStorage[key]);
		},
		get length() {
			return Object.keys(memoryStorage).length;
		},
		key: (index: number) => {
			const keys = Object.keys(memoryStorage);
			return keys[index] || null;
		},
	};

	if (typeof window === "undefined") {
		return memoryStorageImpl;
	}

	// Check if localStorage is available and not null
	if (!isLocalStorageAvailable()) {
		return memoryStorageImpl;
	}

	// localStorage is available, return it
	return window.localStorage;
}

export function getEligibleCutoffDayForLeaseEnd(order: Order): number {
	const lt = order.company.leaseTerm;
	const raw = lt?.eligibleCutoffDate;
	const day = Number(raw);
	if (!Number.isFinite(day) || day <= 0) return 16;
	return day;
}
